import cx from 'classnames';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Input } from 'antd';

import { AppRoutes } from 'config/routes';
import { Button } from 'components/ui/Button';
import { AsideFilter } from './filter/filter';

import { ActionMenu } from 'components/action-menu/action-menu';
import {
  EmployeeStatusText,
  StatusEmployee,
  variantStatus,
} from 'components/status/status-employee';

import { ReactComponent as FilterIcon } from 'assets/images/filter.svg';
import { ReactComponent as SearchIcon } from 'assets/images/search-dashboard.svg';
import { ReactComponent as PlusIcon } from 'assets/images/second-plus.svg';
import { ReactComponent as MoreIcon } from 'assets/images/more.svg';

import { Loader } from 'components/loader/loader';

import { useAppDispatch } from 'store/hooks';
import { useDebounce } from 'usehooks-ts';

import { getFilters, initialValues, validationSchema } from './filter/config';

import { mockData } from '../mock-data';

import s from './AllEmployees.module.scss';

function AllEmployees() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Home, Employees } = AppRoutes.Dashboard;

  const [filter, setFilter] = useState<string>('');
  const filterDebounced = useDebounce(filter, 1000);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [filterStatus, setFilterStatus] = useState<string[]>();

  const roleOptions = mockData.filterRoleList;
  const statusOptions = mockData.filterStatusList;

  const filters = useMemo(
    () => getFilters(t, 'list', { roleOptions, statusOptions }),
    [t, roleOptions, statusOptions],
  );

  const handleFilter = () => {
    setIsFilter((prev) => !prev);
  };

  const handleNewEmployee = () => {
    navigate(Home + Employees.Home + Employees.NewEmployees);
  };

  const handleEmployeeID = (id: string) => {
    navigate(`${Home + Employees.Home}/${id}`);
  };

  const handleFilterValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFilter(value);
  };

  const isBlockedStatus = useCallback((status?: string) => {
    if (!status) return false;
    if (status === EmployeeStatusText.BLOCKED) return true;
    return variantStatus[status as EmployeeStatusText] === 'Blocked';
  }, []);

  const { blockedById, menuById } = useMemo(() => {
    const list = mockData?.employeeList ?? [];

    const blockedByIdObj: Record<string, boolean> = {};
    const menu: Record<string, Array<{ key: string; label: string; onClick: () => void }>> = {};

    const makeToggleHandler = (id: string, block: boolean) => {
      console.log(block ? 'Block' : 'Unblock', id);
    };

    list?.forEach((item) => {
      const blocked = isBlockedStatus(item?.status);

      blockedByIdObj[item?.id] = blocked;

      menu[item?.id] = [
        { key: 'edit', label: t('Edit'), onClick: () => console.log('Edit', item.id) },
        { key: 'delete', label: t('Delete'), onClick: () => console.log('Delete', item.id) },
        {
          key: 'assign',
          label: t('Assigning roles'),
          onClick: () => console.log('Assigning roles', item.id),
        },
        blocked
          ? {
              key: 'unblock',
              label: t('Unblock'),
              onClick: () => makeToggleHandler(item.id, false),
            }
          : {
              key: 'block',
              label: t('Block'),
              onClick: () => makeToggleHandler(item.id, true),
            },
      ];
    });

    return { blockedById: blockedByIdObj, menuById: menu };
  }, [isBlockedStatus, t]);

  const listToRender = mockData?.employeeList ?? [];

  // const filteredEmployees = mockData.employeeList.filter((emp) =>
  //   emp.name.toLowerCase().includes(filterDebounced.toLowerCase()),
  // );

  return (
    <>
      <span className={s['title']}>{t('Employee management')}</span>
      <div className={s['container-content']}>
        <div className={s['header']}>
          <div className={s['wrapper-search']}>
            <div className={s['search']}>
              <Input
                allowClear
                value={filter ?? ''}
                prefix={<SearchIcon />}
                placeholder={t('Search...')}
                onChange={handleFilterValue}
                style={{ borderRadius: '0px', height: '40px' }}
              />
            </div>
            <Button
              icon={<FilterIcon />}
              onClick={handleFilter}
              className={cx(s['button'])}
              displayType="outlined-tetriary"
            >
              {t('Filter')}
            </Button>
          </div>

          <div className={s['wrapper-button']}>
            <Button
              icon={<PlusIcon />}
              onClick={handleNewEmployee}
              className={cx(s['button'], s['button-contract'])}
            >
              {t('New employee')}
            </Button>
          </div>
        </div>
        <div className={s['main']}>
          {listToRender?.map((emp) => {
            const isBlocked = !!blockedById[emp?.id];

            return (
              <div
                key={emp.id}
                className={cx(s['employee'], { [s['employee-blocked']]: isBlocked })}
              >
                <div
                  className={cx(s['employee-row'], { [s['employee-row-blocked']]: isBlocked })}
                >
                  <div className={s['employee-info']}>
                    <span
                      className={cx(s['employee-name'], {
                        [s['employee-name-disabled']]: isBlocked,
                      })}
                      onClick={() => {
                        handleEmployeeID(emp.id);
                      }}
                    >
                      {emp.name}
                    </span>
                    <span className={s['employee-role']}>{emp.role}</span>
                  </div>

                  <div className={s['employee-action']}>
                    <div className={s['employee-status']}>
                      <StatusEmployee status={emp?.status} />
                    </div>

                    <ActionMenu>
                      <ActionMenu.Button>
                        <MoreIcon />
                      </ActionMenu.Button>
                      <ActionMenu.Overlay placement="bottom-end" closeOnItemClick>
                        <div className={s['wrapper-btn']}>
                          {menuById[emp.id]?.map((item) => (
                            <button key={item.key} role="menuitem" onClick={item.onClick}>
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </ActionMenu.Overlay>
                    </ActionMenu>
                  </div>
                </div>
              </div>
            );
          })}
          {/* <Loader size="big" /> */}
        </div>
      </div>
      <AsideFilter
        isOpen={isFilter}
        onSubmitForm={handleFilter}
        filters={filters}
        onClose={() => setIsFilter(false)}
        validationSchemaOverride={validationSchema(t, 'list')}
        initialValuesOverride={initialValues('list')}
      />
    </>
  );
}

export default AllEmployees;
